import React, { useState, useEffect } from 'react';
import './PartnerDetailsModal.css';

const PartnerDetailsModal = ({ partnerId, partnerName, onClose }) => {
    const [partnerData, setPartnerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [error, setError] = useState(null);

    // Cargar datos detallados del partner
    useEffect(() => {
        const fetchPartnerDetails = async () => {
            setLoading(true);
            setError(null);

            try {
                const token = localStorage.getItem('token');

                // Obtener datos en paralelo
                const [
                    resumenResponse,
                    enlacesResponse,
                    gananciasResponse,
                    referidosResponse,
                    proveedoresResponse
                ] = await Promise.all([
                    fetch(`https://api.minymol.com/earnings/resumen`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`https://api.minymol.com/earnings/enlaces`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`https://api.minymol.com/earnings/tabla-ganancias?page=1&limit=10`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`https://api.minymol.com/earnings/referidos`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`https://api.minymol.com/earnings/proveedores`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                const [resumen, enlaces, ganancias, referidos, proveedores] = await Promise.all([
                    resumenResponse.ok ? resumenResponse.json() : null,
                    enlacesResponse.ok ? enlacesResponse.json() : [],
                    gananciasResponse.ok ? gananciasResponse.json() : [],
                    referidosResponse.ok ? referidosResponse.json() : [],
                    proveedoresResponse.ok ? proveedoresResponse.json() : []
                ]);

                setPartnerData({
                    resumen,
                    enlaces,
                    ganancias,
                    referidos,
                    proveedores
                });
            } catch (error) {
                console.error('Error fetching partner details:', error);
                setError('Error al cargar los detalles del partner');
            } finally {
                setLoading(false);
            }
        };

        if (partnerId) {
            fetchPartnerDetails();
        }
    }, [partnerId]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Aquí podrías agregar una notificación toast
    };

    if (loading) {
        return (
            <div className="partner-details-modal-overlay">
                <div className="partner-details-modal">
                    <div className="partner-details-modal-header">
                        <h2>🔄 Cargando detalles...</h2>
                        <button className="partner-details-modal-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="partner-details-loading-content">
                        <div className="partner-details-spinner"></div>
                        <p>Obteniendo información del partner</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="partner-details-modal-overlay">
                <div className="partner-details-modal">
                    <div className="partner-details-modal-header">
                        <h2>❌ Error</h2>
                        <button className="partner-details-modal-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="partner-details-error-content">
                        <p>{error}</p>
                        <button className="partner-details-btn-retry" onClick={() => window.location.reload()}>
                            Reintentar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="partner-details-modal-overlay">
            <div className="partner-details-modal">
                <div className="partner-details-modal-header">
                    <h2>👤 Detalles de {partnerName}</h2>
                    <button className="partner-details-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="partner-details-modal-tabs">
                    <button
                        className={`partner-details-tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        📊 Resumen
                    </button>
                    <button
                        className={`partner-details-tab ${activeTab === 'earnings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('earnings')}
                    >
                        💰 Ganancias
                    </button>
                    <button
                        className={`partner-details-tab ${activeTab === 'referrals' ? 'active' : ''}`}
                        onClick={() => setActiveTab('referrals')}
                    >
                        👥 Referidos
                    </button>
                    <button
                        className={`partner-details-tab ${activeTab === 'links' ? 'active' : ''}`}
                        onClick={() => setActiveTab('links')}
                    >
                        🔗 Enlaces
                    </button>
                    {partnerData?.proveedores && partnerData.proveedores.length > 0 && (
                        <button
                            className={`partner-details-tab ${activeTab === 'providers' ? 'active' : ''}`}
                            onClick={() => setActiveTab('providers')}
                        >
                            🏪 Proveedores
                        </button>
                    )}
                </div>

                <div className="partner-details-modal-content">
                    {/* Tab: Resumen */}
                    {activeTab === 'overview' && partnerData?.resumen && (
                        <div className="partner-details-tab-content">
                            <div className="partner-details-overview-grid">
                                <div className="partner-details-stat-card">
                                    <h3>💰 Ganancias por Referidos</h3>
                                    <p className="stat-value">{formatCurrency(partnerData.resumen.referidos)}</p>
                                </div>
                                <div className="partner-details-stat-card">
                                    <h3>🎯 Ganancias SAI</h3>
                                    <p className="stat-value">{formatCurrency(partnerData.resumen.sai)}</p>
                                </div>
                                <div className="partner-details-stat-card">
                                    <h3>🏪 Ganancias Afiliaciones</h3>
                                    <p className="stat-value">{formatCurrency(partnerData.resumen.afiliaciones)}</p>
                                </div>
                                <div className="partner-details-stat-card total">
                                    <h3>💎 Total Generado</h3>
                                    <p className="stat-value">
                                        {formatCurrency(
                                            partnerData.resumen.referidos +
                                            partnerData.resumen.sai +
                                            partnerData.resumen.afiliaciones
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab: Ganancias */}
                    {activeTab === 'earnings' && (
                        <div className="partner-details-tab-content">
                            <h3>💰 Historial de Ganancias</h3>
                            {partnerData?.ganancias && partnerData.ganancias.length > 0 ? (
                                <div className="partner-details-earnings-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Código</th>
                                                <th>Monto</th>
                                                <th>Fecha</th>
                                                <th>Tipo</th>
                                                <th>Estado</th>
                                                <th>Referido</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {partnerData.ganancias.map((ganancia, index) => (
                                                <tr key={index}>
                                                    <td>{ganancia.id}</td>
                                                    <td className="currency">{formatCurrency(ganancia.monto)}</td>
                                                    <td>{ganancia.fecha}</td>
                                                    <td>
                                                        <span className={`partner-details-type-badge partner-details-type--${ganancia.tipo.toLowerCase().replace(' ', '-')}`}>
                                                            {ganancia.tipo}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`partner-details-status-badge partner-details-status--${ganancia.estado.toLowerCase()}`}>
                                                            {ganancia.estado}
                                                        </span>
                                                    </td>
                                                    <td>{ganancia.referido}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="partner-details-no-data">
                                    <p>📈 No hay ganancias registradas aún</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab: Referidos */}
                    {activeTab === 'referrals' && (
                        <div className="partner-details-tab-content">
                            <h3>👥 Referidos</h3>
                            {partnerData?.referidos && partnerData.referidos.length > 0 ? (
                                <div className="partner-details-referrals-grid">
                                    {partnerData.referidos.map((referido, index) => (
                                        <div key={index} className="partner-details-referral-card">
                                            <div className="partner-details-referral-header">
                                                <h4>{referido.name}</h4>
                                                <span className={`partner-details-referral-type partner-details-type--${referido.type.toLowerCase()}`}>
                                                    {referido.type}
                                                </span>
                                            </div>
                                            <p className="partner-details-referral-details">{referido.details}</p>
                                            <div className="partner-details-referral-meta">
                                                <span className="partner-details-referral-date">📅 {referido.date}</span>
                                                <span className={`partner-details-referral-status partner-details-status--${referido.status.toLowerCase().replace(' ', '-')}`}>
                                                    {referido.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="partner-details-no-data">
                                    <p>👤 No hay referidos registrados aún</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab: Enlaces */}
                    {activeTab === 'links' && (
                        <div className="partner-details-tab-content">
                            <h3>🔗 Enlaces de Referidos</h3>
                            {partnerData?.enlaces && partnerData.enlaces.length > 0 ? (
                                <div className="partner-details-links-list">
                                    {partnerData.enlaces.map((enlace) => (
                                        <div key={enlace.id} className="partner-details-link-card">
                                            <div className="partner-details-link-header">
                                                <h4>{enlace.descripcion}</h4>
                                                <span className="partner-details-link-earning">{formatCurrency(enlace.ganancia_por_referido)}</span>
                                            </div>
                                            <div className="partner-details-link-url">
                                                <input
                                                    type="text"
                                                    value={enlace.url_completa}
                                                    readOnly
                                                    className="partner-details-link-input"
                                                />
                                                <button
                                                    onClick={() => copyToClipboard(enlace.url_completa)}
                                                    className="partner-details-btn-copy"
                                                    title="Copiar enlace"
                                                >
                                                    📋
                                                </button>
                                            </div>
                                            <div className="partner-details-link-stats">
                                                <span>Usos: {enlace.usos_totales}</span>
                                                <span>Último uso: {enlace.ultimo_uso ? formatDate(enlace.ultimo_uso) : 'Nunca'}</span>
                                                <span className={`partner-details-link-status ${enlace.activo ? 'active' : 'inactive'}`}>
                                                    {enlace.activo ? '✅ Activo' : '❌ Inactivo'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="partner-details-no-data">
                                    <p>🔗 No hay enlaces de referidos configurados</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab: Proveedores */}
                    {activeTab === 'providers' && partnerData?.proveedores && (
                        <div className="partner-details-tab-content">
                            <h3>🏪 Proveedores Referidos</h3>
                            {partnerData.proveedores.length > 0 ? (
                                <div className="partner-details-providers-grid">
                                    {partnerData.proveedores.map((proveedor, index) => (
                                        <div key={index} className="partner-details-provider-card">
                                            <div className="partner-details-provider-header">
                                                <h4>{proveedor.name}</h4>
                                                <span className="partner-details-provider-earning">{formatCurrency(proveedor.ganado)}</span>
                                            </div>
                                            <p className="partner-details-provider-details">{proveedor.details}</p>
                                            <div className="partner-details-provider-stats">
                                                <span>{proveedor.pastInteractions}</span>
                                                <span className={`partner-details-provider-status partner-details-status--${proveedor.status.toLowerCase().replace(' ', '-')}`}>
                                                    {proveedor.status}
                                                </span>
                                            </div>
                                            <div className="partner-details-provider-meta">
                                                <small>Registro: {proveedor.joinDate}</small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="partner-details-no-data">
                                    <p>🏪 No hay proveedores referidos aún</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnerDetailsModal;
